/**
 * Page wrapper Sales Control — bám `form_frmCtrlSalesMain.txt` (header + 10 nav tabs).
 *
 * - Header filters/exports khớp VBA: `Shipment Period`, `Is Finalized`, `Export (All)`,
 *   `Export (Filter)` cùng một hàng.
 * - Nav tabs: 10 tab theo đúng thứ tự Access (`navCtrlSalesLocalS` → `Preferences`).
 * - Mỗi tab render component riêng trong `frontend/src/pages/saleControl/`.
 */
import dayjs from 'dayjs'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import SaleReferencesView from '@/pages/saleContract/SaleReferencesView'
import SaleControlReferencesView from '@/components/references/OutboundReferencesView'
import { REFERENCE_CATALOG, type ReferencePageKey } from '@/components/references/catalog'

// Filter enable/disable per reference page giờ do config (SALE_CONTROL_REFERENCE_PAGE_FILTERS)
// tự khai — không hardcode list ở đây nữa.
// Reference page mà bộ lọc ngày = Shipment Period (lọc theo shipment của HĐ bán)
// → dùng label "Shipment Period" thay vì "Date Period".
const SHIPMENT_PERIOD_LABEL_REFS: ReferencePageKey[] = ['amendment', 'ptbf-fixation-sale']
import PageHeader from '@/components/PageHeader'
import ReloadButton from '@/components/ReloadButton'
import {
  CARD_CONTENT_COMPACT,
  FILTER_HEADER_INPUT_SX,
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_TABLET_INLINE,
} from '@/theme/responsive'
import { usePageFilters } from '@/hooks/usePageFilters'
import { usePreference } from '@/hooks/usePreference'
import { useAppSelector } from '@/store'
import { API_BASE_URL } from '@/config/api'
import {
  SALE_CONTROL_EXPORT_TOKEN,
  type SaleControlFinalizedFilter,
} from '@/store/api/saleControlApi'

import ComingSoonPage from '@/pages/common/ComingSoonPage'
import { getUnbuild } from '@/config/pageBuildStatus'
import SaleControlClaimView from '@/pages/saleControl/SaleControlClaimView'
import SaleControlCreditNoteView from '@/pages/saleControl/SaleControlCreditNoteView'
import SaleControlDebitNoteView from '@/pages/saleControl/SaleControlDebitNoteView'
import SaleControlDeliveryOrderView from '@/pages/saleControl/SaleControlDeliveryOrderView'
import SaleControlInvoiceView from '@/pages/saleControl/SaleControlInvoiceView'
import SaleControlLocalSalesView from '@/pages/saleControl/SaleControlLocalSalesView'
import SaleControlMovingView from '@/pages/saleControl/SaleControlMovingView'
import SaleControlReceiveOrderView from '@/pages/saleControl/SaleControlReceiveOrderView'
import SaleControlSalesAllocationView from '@/pages/saleControl/SaleControlSalesAllocationView'
import {
  SALE_CONTROL_TAB_CONFIG,
  getSaleControlTab,
  isSaleControlTabKey,
  type SaleControlTabKey,
} from '@/pages/saleControl/saleControlTabConfig'

interface SaleControlFilters extends Record<string, unknown> {
  startDate: string
  endDate: string
  isFinalized: SaleControlFinalizedFilter
}

const FILTER_PAGE_KEY = 'sales-control'

function getDefaultFilters(): SaleControlFilters {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
    isFinalized: 'all',
  }
}

function normalizeStoredFilters(raw: unknown): SaleControlFilters {
  const defaults = getDefaultFilters()
  if (!raw || typeof raw !== 'object') return defaults
  const parsed = raw as Partial<SaleControlFilters>
  return {
    startDate: typeof parsed.startDate === 'string' && parsed.startDate ? parsed.startDate : defaults.startDate,
    endDate: typeof parsed.endDate === 'string' && parsed.endDate ? parsed.endDate : defaults.endDate,
    isFinalized:
      parsed.isFinalized === 'yes' || parsed.isFinalized === 'no' || parsed.isFinalized === 'all'
        ? parsed.isFinalized
        : 'all',
  }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

// All Sales Control tabs are parked behind ComingSoonPage until the rebuild lands.
// Original view components (`SaleControlLocalSalesView`, `SaleControlSalesAllocationView`,
// `SaleControlDeliveryOrderView`, `SaleControlInvoiceView`, `SaleControlReceiveOrderView`,
// `SaleControlClaimView`, `SaleControlCreditNoteView`, `SaleControlDebitNoteView`,
// `SaleControlMovingView`, `SaleReferencesView`) + local-sales RTK selectors
// (`localSalesQueryArgs`, `localSalesSelect`, `localSalesFetching`) are still imported
// so restoring a tab is just swapping the body of renderTab back to the per-key switch.
//
// Original switch body (pre-park, restore as-is when rebuilding):
//   switch (key) {
//     case 'sales-contract':
//       return (
//         <SaleControlLocalSalesView
//           startDate={filters.startDate}
//           endDate={filters.endDate}
//           isFinalized={filters.isFinalized}
//           invalidRange={invalidRange}
//         />
//       )
//     case 'sales-allocation':  return <SaleControlSalesAllocationView />
//     case 'delivery-order':    return <SaleControlDeliveryOrderView />
//     case 'invoice':           return <SaleControlInvoiceView />
//     case 'receive-order':     return <SaleControlReceiveOrderView />
//     case 'claim':             return <SaleControlClaimView />
//     case 'credit-note':       return <SaleControlCreditNoteView />
//     case 'debit-note':        return <SaleControlDebitNoteView />
//     case 'moving':            return <SaleControlMovingView />
//     case 'references':        return <SaleReferencesView />
//     default:                  return null
//   }
function renderTab(
  key: SaleControlTabKey,
  filters: SaleControlFilters,
  invalidRange: boolean,
  refPageKey: ReferencePageKey,
  onRefPageKeyChange: (k: ReferencePageKey) => void,
) {
  const unbuild = getUnbuild(`/sales/control/${key}`)
  if (unbuild) {
    return <ComingSoonPage title={getSaleControlTab(key).label} embedded status={unbuild.status} />
  }
  if (key === 'sales-contract') {
    return (
      <SaleControlLocalSalesView
        startDate={filters.startDate}
        endDate={filters.endDate}
        isFinalized={filters.isFinalized}
        invalidRange={invalidRange}
      />
    )
  }
  if (key === 'delivery-order') {
    return (
      <SaleControlDeliveryOrderView
        shipmentPeriodStart={filters.startDate}
        shipmentPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'sales-allocation') {
    return (
      <SaleControlSalesAllocationView
        shipmentPeriodStart={filters.startDate}
        shipmentPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'invoice') {
    return (
      <SaleControlInvoiceView
        deliveryPeriodStart={filters.startDate}
        deliveryPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'receive-order') {
    return (
      <SaleControlReceiveOrderView
        shipmentPeriodStart={filters.startDate}
        shipmentPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'credit-note') {
    return (
      <SaleControlCreditNoteView
        deliveryPeriodStart={filters.startDate}
        deliveryPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'claim') {
    return (
      <SaleControlClaimView
        shipmentPeriodStart={filters.startDate}
        shipmentPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'debit-note') {
    return (
      <SaleControlDebitNoteView
        deliveryPeriodStart={filters.startDate}
        deliveryPeriodEnd={filters.endDate}
        finalizedFilter={filters.isFinalized}
      />
    )
  }
  if (key === 'moving') {
    return <SaleControlMovingView />
  }
  if (key === 'references') {
    return (
      <SaleControlReferencesView
        pageKey={refPageKey}
        onPageKeyChange={onRefPageKeyChange}
        startDate={filters.startDate}
        endDate={filters.endDate}
        isFinalized={filters.isFinalized}
      />
    )
  }
  return <ComingSoonPage title={getSaleControlTab(key).label} embedded />
}

export default function SaleControlPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const { filters: storedFilters, setFilters: persistFilters } = usePageFilters<SaleControlFilters>(FILTER_PAGE_KEY)
  const filters = useMemo(() => normalizeStoredFilters(storedFilters), [storedFilters])
  const setFilters = persistFilters as (next: SaleControlFilters | ((prev: SaleControlFilters | undefined) => SaleControlFilters)) => void
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  const navPref = usePreference<{ lastTab: string }>('sale_control', 'navigation', undefined, { refetchOnMount: true })

  const activeKey = useMemo<SaleControlTabKey>(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[2]
    return isSaleControlTabKey(candidate) ? candidate : 'sales-contract'
  }, [location.pathname])

  // Bare-route (URL không có sub-tab hợp lệ) → restore tab cuối user đã mở qua
  // preferences registry (nếu đã fetch xong). Fallback `sales-contract`.
  useEffect(() => {
    if (!location.pathname.startsWith('/sales/control')) return

    const segments = location.pathname.split('/').filter(Boolean)
    if (isSaleControlTabKey(segments[2])) return
    if (navPref.isLoading || navPref.isFetching) return
    const saved = navPref.value?.lastTab
    const target = saved && isSaleControlTabKey(saved) ? saved : 'sales-contract'
    navigate(`/sales/control/${target}`, { replace: true })
  }, [location.pathname, navigate, navPref.isLoading, navPref.isFetching, navPref.value])

  // Auto-save tab đang mở (debounce 400ms để click nhanh không spam BE).
  useEffect(() => {
    if (navPref.isLoading || navPref.isFetching) return
    const urlTab = location.pathname.split('/').filter(Boolean)[2]
    if (!isSaleControlTabKey(urlTab)) return
    if (navPref.value?.lastTab === urlTab) return
    void navPref.patch({ lastTab: urlTab }).catch(() => undefined)
  }, [location.pathname, navPref.value?.lastTab, navPref.patch])

  const activeTab = SALE_CONTROL_TAB_CONFIG.find((tab) => tab.key === activeKey) ?? SALE_CONTROL_TAB_CONFIG[0]
  const hasInvalidRange = filters.startDate > filters.endDate
  // Reference page đang chọn (lifted từ SaleControlReferencesView) → header
  // enable/disable filter theo từng page:
  //   ServiceInvoices: disable cả Date Period lẫn Is Finalized.
  //   VirtualWH:       enable Date Period (lọc date_in), disable Is Finalized.
  //   EXCalendar:      disable cả Date Period lẫn Is Finalized.
  //   Annex:           enable cả 2 (Shipment Period + Is Finalized) lọc theo HĐ.
  const gotoPref = usePreference<{ lastPage: string }>('sale_control', 'goto_page', undefined, { refetchOnMount: true })
  const refPageKey = ((): ReferencePageKey => {
    const v = gotoPref.value?.lastPage
    return v && v in REFERENCE_CATALOG ? (v as ReferencePageKey) : 'service-invoices'
  })()
  const setRefPageKey = (k: ReferencePageKey) => {
    void gotoPref.patch({ lastPage: k }).catch(() => undefined)
  }
  const isRefTab = activeKey === 'references'
  // Filter do page tự khai (catalog) → header chỉ enable đúng filter page cần.
  const refFilters = REFERENCE_CATALOG[refPageKey].filters ?? []
  const disableDatePeriod = isRefTab && !refFilters.includes('date')
  const disableIsFinalized = isRefTab && !refFilters.includes('finalized')
  // References tab mặc định "Date Period"; riêng page lọc theo shipment của HĐ
  // (Annex) dùng "Shipment Period". Các tab khác luôn "Shipment Period".
  const datePeriodLabel =
    isRefTab && !SHIPMENT_PERIOD_LABEL_REFS.includes(refPageKey)
      ? 'Date Period'
      : 'Shipment Period'

  // Nút Reload dùng chung phải tác động ĐÚNG list của tab đang active. Mỗi tab có
  // cache tag + endpoint riêng — Delivery Order dùng tag `SaleDeliveryOrder` (KHÔNG
  // phải `SaleControl`), nên hardcode 1 tag sẽ bỏ sót tab đó. Map theo tab:
  // `apiTags` = tag list query của tab → invalidate đúng query; `watch` = tên
  // endpoint → spinner "busy" khi query đó đang pending (theo tab).
  const reloadConfig = useMemo<{ apiTags: string[]; watch: string[] }>(() => {
    switch (activeKey) {
      case 'delivery-order':
        return { apiTags: ['SaleDeliveryOrder'], watch: ['getSaleDeliveryOrders'] }
      case 'invoice':
        return { apiTags: ['SaleInvoice'], watch: ['getSaleInvoices'] }
      case 'receive-order':
        return { apiTags: ['SaleReleaseOrder'], watch: ['getSaleReleaseOrders'] }
      case 'credit-note':
        return { apiTags: ['SaleCreditNote'], watch: ['getSaleCreditNotes'] }
      case 'debit-note':
        return { apiTags: ['SaleDebitNote'], watch: ['getSaleDebitNotes'] }
      case 'claim':
        return { apiTags: ['SaleClaim'], watch: ['getSaleClaims'] }
      case 'moving':
        return { apiTags: ['SaleMoving'], watch: ['getSaleMovings'] }
      case 'sales-allocation':
        return { apiTags: ['SaleControl'], watch: ['getCtrlSalesSaleAlc'] }
      case 'sales-contract':
        return { apiTags: ['SaleControl'], watch: ['getCtrlSalesLocalS'] }
      case 'references':
        return { apiTags: ['SaleControlReference'], watch: ['getSaleControlReferenceRows'] }
      default:
        return { apiTags: ['SaleControl'], watch: [] }
    }
  }, [activeKey])

  const downloadSaleControlExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'SaleControl_filter.xlsx' : 'SaleControl.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  /** Access `btnExportExcel_Click` — `/download/salecontrols/data`. */
  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/salecontrols/data?token=${encodeURIComponent(SALE_CONTROL_EXPORT_TOKEN)}&email=${encodeURIComponent(email)}`
    await downloadSaleControlExcel('all', url)
  }

  /** Access `btnExportExcellFilter_Click` — `/download/salecontrols/data_filter`. */
  const handleExportFilter = async () => {
    const params = new URLSearchParams({
      token: SALE_CONTROL_EXPORT_TOKEN,
      email,
      start_date: filters.startDate,
      end_date: filters.endDate,
      is_forced_close: filters.isFinalized === 'all' ? 'All' : filters.isFinalized === 'yes' ? 'Yes' : 'No',
    })
    const url = `${API_BASE_URL}/erp-legacy/download/salecontrols/data_filter?${params.toString()}`
    await downloadSaleControlExcel('filter', url)
  }

  const headerButtonSx = {
    minHeight: 24,
    px: 0.75,
    py: 0.15,
    fontSize: '0.67rem',
    lineHeight: 1.1,
    textTransform: 'none' as const,
  }


  // Resolve-first: URL chưa có sub-tab hợp lệ (đang đọc DB + chờ restore) → hiện
  // loading, KHÔNG render Page default. Có Page đúng trên URL mới render.
  if (!isSaleControlTabKey(location.pathname.split('/').filter(Boolean)[2]))
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Sales Control"
        module="Sales"
        actions={
          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            {/* Pattern 11 date range convention 2026-05-28:
                  - DatePicker đầu: label "Shipment Period"
                  - Icon `→` giữa phân biệt from → to
                  - DatePicker thứ 2: KHÔNG label
                  - aria-label trên cả 2 cho screen reader. */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={datePeriodLabel}
                disabled={disableDatePeriod}
                format="DD-MMM-YYYY"
                value={filters.startDate ? dayjs(filters.startDate) : null}
                onChange={(d) =>
                  setFilters((prev) => ({
                    ...getDefaultFilters(),
                    ...prev,
                    startDate: d ? d.format('YYYY-MM-DD') : getDefaultFilters().startDate,
                  }))
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: FILTER_HEADER_INPUT_SX,
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': `${datePeriodLabel} start date` },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
                aria-hidden="true"
              />
              <DatePicker
                disabled={disableDatePeriod}
                format="DD-MMM-YYYY"
                value={filters.endDate ? dayjs(filters.endDate) : null}
                onChange={(d) =>
                  setFilters((prev) => ({
                    ...getDefaultFilters(),
                    ...prev,
                    endDate: d ? d.format('YYYY-MM-DD') : getDefaultFilters().endDate,
                  }))
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: FILTER_HEADER_INPUT_SX,
                    inputProps: { 'aria-label': `${datePeriodLabel} end date` },
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              select
              label="Is Finalized"
              size="small"
              disabled={disableIsFinalized}
              value={filters.isFinalized}
              onChange={(event) =>
                setFilters((prev) => ({ ...getDefaultFilters(), ...prev, isFinalized: event.target.value as SaleControlFinalizedFilter }))
              }
              InputLabelProps={{ shrink: true }}
              sx={FILTER_HEADER_SELECT_SX}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>

            <ReloadButton
              sx={headerButtonSx}
              apiTags={reloadConfig.apiTags}
              baseTags={[]}
              watchEndpoints={reloadConfig.watch}
            />
            {/* Pattern 10: 2 button cùng icon Download → discriminator Filter/All
                luôn show; "Export (" + ")" wrap ẩn xs+sm → xs+sm chỉ "Filter"/"All". */}
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportFilter}
              disabled={exporting !== null || !email || hasInvalidRange}
              title="Export (Filter)"
              startIcon={
                exporting === 'filter' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export (</Box>
              Filter
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportAll}
              disabled={exporting !== null || !email}
              title="Export (All)"
              startIcon={
                exporting === 'all' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export (</Box>
              All
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
            </Button>
          </Stack>
        }
      />
      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: SaleControlTabKey) => {
            void navPref.patch({ lastTab: value }).catch(() => undefined)
            navigate(`/sales/control/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              // vạch ngăn dọc ngắn, căn giữa, mảnh giữa các tab — ẩn cạnh tab đang chọn/hover cho sạch
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {SALE_CONTROL_TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={CARD_CONTENT_COMPACT}>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            {renderTab(activeKey, filters, hasInvalidRange, refPageKey, setRefPageKey)}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
